import { logAppInfo } from "@/utils/errors/errorLogger";
import { getGeocode } from "@shared/lib/google";
import { ParsedUrlQuery } from "querystring";

export const possibleFilterParams: Record<string, boolean | number | string> = {
  q: 1,
  search_string: 1,
  tags: 1,
  ends_on__gte: 1,
  source_channel: 1,
  starts_on__lte: 1,
  categories: 1,
  voucher_code: 1,
  id__in: 1,
  "id__in!": 1,
  venue__in: 1,
  showpass_gift_card: true,
  ordering: "-is_sponsored,-ranking_factor,starts_on",
};

/// Retrieve an object with all search page query parameters _not_ relevant to an event search API call removed
export const cleanFilterParams = (obj: ParsedUrlQuery): ParsedUrlQuery => {
  if (!obj) return {};

  const newParams: ParsedUrlQuery = {};

  Object.keys(obj).forEach((key) => {
    if (possibleFilterParams[key]) {
      newParams[key] = obj[key]; // Only keep allowed params
    }
  });

  return newParams;
};

/**
 * Transform an object containing event search page query parameters (/s/events/) to an object containing the relevant parameters for the /api/public/discovery/ call:
 *
 *  - categories?: {string,string,...}
 *  - ends_on__gte?: {date} // end date of search window
 *  - location__point_location?: {lat,lon} // if a location has been provided
 *  - ordered__location__point_location?: {lat,lon} // if sorting by distance
 *  - only_parents?: {bool}
 *  - source_referer?: {string} // the domain name that linked to the showpass site
 *  - q?: {string} // event name
 *  - search_string: {} // event or venue name -> q converts to this in this method since this should only be used with discovery view.
 *  - source_channel?: {"discovery"|"non_discovery"} // customer journey
 *  - starts_on_lte?: {date} // start date of search window
 *  - tags?: {string,string,...}
 *
 * In the event that a physical location has been selected as a search facet (ie: query.location != 'all'),
 * geocoding will convert the string to a lat/long value, ex: "?location=New%20York,%20New%20York" → "location__point_location:40.7127753,-74.0059728,100"
 *
 * @returns {object}
 */
export const getEventQueryParams = async (
  query: ParsedUrlQuery
): Promise<ParsedUrlQuery> => {
  if (!query) {
    return {};
  }

  const { location, sortBy } = query;

  if (location === undefined) {
    return {};
  }

  // Create a copy of the query object to avoid direct mutation
  const queryCopy = { ...query };
  const queryCheck = { ...query };
  delete queryCheck.location; // Location should not be taken into account for the queryCheck

  // Convert `q` to `search_string` and remove `q`
  if (queryCopy.q) {
    queryCopy.search_string = queryCopy.q;
    delete queryCopy.q;
  }

  let payload = cleanFilterParams(queryCopy);

  const discovery =
    payload.showpass_source ||
    (query.q || "").toString() === "" ||
    query.source_channel === "discovery";

  payload = Object.assign(payload, {
    source_channel: discovery ? "discovery" : "non_discovery",
  });

  if (
    typeof document !== "undefined" &&
    document.referrer.length > 0 &&
    document.referrer.indexOf("showpass.com") < 0
  ) {
    payload.source_referer = document.referrer;
  }

  if (location === "all") {
    return payload;
  }
  logAppInfo("loading location geocode");

  try {
    const { results } = await getGeocode({
      address: location.toString(),
    });
    if (!results) {
      throw new Error("Unable to resolve location");
    }
    const { geometry } = results[0];

    const location__point_location = `${geometry.location.lat},${geometry.location.lng},100`;

    if (sortBy && sortBy === "distance") {
      return {
        ...payload,
        ordered__location__point_location: location__point_location,
      };
    }
    return {
      ...payload,
      location__point_location,
    };
  } catch (e) {
    logAppInfo("error loading location geocode");
    logAppInfo((e as Error).message);
    return payload;
  }
};
